import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { queryKeys } from "@/services/queryKeyFactory";
import { getAuditLog, type AuditLogEntry } from "@/services/auditLogService";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 25;
const ACTION_OPTIONS = [
  "impersonation_started",
  "impersonation_stopped",
  "setting_created",
  "setting_updated",
  "setting_deleted",
  "mentee_reassigned",
  "mentee_unassigned",
  "match_regenerated",
  "preference_edited",
  "preference_deleted",
  "deadline_extension_added",
  "deadline_extension_removed",
  "user_created",
  "user_updated",
  "user_deleted",
] as const;
const ENTITY_OPTIONS = ["setting", "mentee", "preference", "suggestion", "user", "deadline_extension"] as const;

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatValue(val: unknown): string {
  if (val == null) return "—";
  if (typeof val === "string") return val.length > 80 ? val.slice(0, 80) + "…" : val;
  try {
    const s = JSON.stringify(val);
    return s.length > 80 ? s.slice(0, 80) + "…" : s;
  } catch {
    return String(val);
  }
}

const AuditLogPage = () => {
  const { t } = useTranslation();
  const [actionFilter, setActionFilter] = useState<string>("");
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [page, setPage] = useState(0);

  const params = useMemo(
    () => ({
      action: actionFilter || undefined,
      entityType: entityTypeFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [actionFilter, entityTypeFilter, fromDate, toDate, page]
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.auditLog(params),
    queryFn: () => getAuditLog(params),
  });

  const entries = data?.entries ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasNext = page < totalPages - 1;
  const hasPrev = page > 0;

  return (
    <>
      <div className="container py-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("auditLog.title")}</CardTitle>
            <CardDescription>{t("auditLog.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-text-2">{t("auditLog.filterAction")}</label>
                <Select value={actionFilter || "all"} onValueChange={(v) => setActionFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t("auditLog.filterAction")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {ACTION_OPTIONS.map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-text-2">{t("auditLog.filterEntityType")}</label>
                <Select value={entityTypeFilter || "all"} onValueChange={(v) => setEntityTypeFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t("auditLog.filterEntityType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {ENTITY_OPTIONS.map((e) => (
                      <SelectItem key={e} value={e}>
                        {e}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-text-2">{t("auditLog.fromDate")}</label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPage(0);
                  }}
                  className="w-[160px]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-text-2">{t("auditLog.toDate")}</label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPage(0);
                  }}
                  className="w-[160px]"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => setPage(0)}>
                {t("auditLog.applyFilters")}
              </Button>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {isError && (
              <p className="text-sm text-destructive">
                {t("common.errorDesc")} {(error as Error)?.message}
              </p>
            )}
            {!isLoading && !isError && (
              <>
                <div className="rounded-md border overflow-x-auto">
                  <Table className="min-w-[900px]">
                    <TableHeader className="bg-bg-elevated sticky top-0 z-10">
                      <TableRow>
                        <TableHead className="w-[160px] text-xs font-semibold text-text-2">
                          {t("auditLog.when")}
                        </TableHead>
                        <TableHead className="w-[180px] text-xs font-semibold text-text-2">
                          {t("auditLog.who")}
                        </TableHead>
                        <TableHead className="w-[160px] text-xs font-semibold text-text-2">
                          {t("auditLog.action")}
                        </TableHead>
                        <TableHead className="w-[130px] text-xs font-semibold text-text-2">
                          {t("auditLog.entityType")}
                        </TableHead>
                        <TableHead className="w-[90px] text-xs font-semibold text-text-2">
                          {t("auditLog.entityId")}
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-text-2">
                          {t("auditLog.beforeAfter")}
                        </TableHead>
                        <TableHead className="max-w-[220px] text-xs font-semibold text-text-2">
                          {t("auditLog.details")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-text-2 py-8">
                            {t("auditLog.noEntries")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        entries.map((entry: AuditLogEntry) => (
                          <TableRow key={entry.id} className="hover:bg-muted/40">
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {formatDate(entry.createdAt)}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{entry.userFullName || entry.userEmail}</span>
                              {entry.userFullName && (
                                <span className="block text-xs text-text-2 truncate max-w-[180px]">
                                  {entry.userEmail}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-mono text-[11px]">
                                {entry.action}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {entry.entityType ? (
                                <Badge variant="outline" className="text-[11px]">
                                  {entry.entityType}
                                </Badge>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">{entry.entityId ?? "—"}</TableCell>
                            <TableCell className="text-xs max-w-[260px]">
                              {entry.beforeValue != null || entry.afterValue != null ? (
                                <div className="space-y-1 text-muted-foreground">
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80">
                                      Before
                                    </span>
                                    <div className="font-mono text-[11px]">{formatValue(entry.beforeValue)}</div>
                                  </div>
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80">
                                      After
                                    </span>
                                    <div className="font-mono text-[11px]">{formatValue(entry.afterValue)}</div>
                                  </div>
                                </div>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-text-2 max-w-[220px] truncate" title={entry.details ?? ""}>
                              {entry.details ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-2">
                      {t("auditLog.showing")} {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} {t("auditLog.of")} {total}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={!hasPrev} onClick={() => setPage((p) => p - 1)}>
                        <ChevronLeft className="h-4 w-4" />
                        {t("common.previous")}
                      </Button>
                      <Button variant="outline" size="sm" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
                        {t("common.next")}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default AuditLogPage;
